import { useQuery } from "@tanstack/react-query";
import useFetch from "./useFetch";
import { useDebounce } from "./useDebounce";
import { EntityType, SearchContext, SearchResponse } from "@/types/search";

interface UseSearchOptions {
  query: string;
  entityTypes?: EntityType[];
  byId?: boolean;
  enabled?: boolean;
  debounceMs?: number;
  context?: SearchContext;
}

export function useSearch({
  query,
  entityTypes,
  byId = false,
  enabled = true,
  debounceMs = 200,
  context,
}: UseSearchOptions) {
  const { doFetch } = useFetch();
  const debouncedQuery = useDebounce(query, debounceMs);

  const shouldFetch = enabled && debouncedQuery.length >= 2;

  const typesParam = entityTypes?.join(",") || undefined;

  return useQuery<SearchResponse, Error>({
    queryKey: ["search", debouncedQuery, typesParam, byId, context],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set("q", debouncedQuery);
      if (typesParam) {
        params.set("types", typesParam);
      }
      if (byId) {
        params.set("by_id", "true");
      }
      if (context) {
        params.set("context", context);
      }
      params.set("limit", "20");

      const url = `/search?${params.toString()}`;
      const result = await doFetch<SearchResponse>(url);
      if (!result) {
        throw new Error("Search failed");
      }
      return result;
    },
    enabled: shouldFetch,
    staleTime: 1000 * 60, // 1 minute
    gcTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
}
